import { MESSAGE_TYPE } from 'shared/constants/messages';
import { applyPageFilters, applyRoleFilter, sortComparator } from './helpers';
import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator';
import { matchesFilters } from './helpers/filterHelpers';
import {
  getUserPermissions,
  getUserRole,
} from '../../../helper/permissionsHelper';
import camelcaseKeys from 'camelcase-keys';

export const getSelectedChatConversation = ({
  allConversations,
  selectedChatId,
}) =>
  allConversations.filter(conversation => conversation.id === selectedChatId);

// A triagem ao vivo acontece com a conversa em `pending`, e o filtro padrão da
// lista é `open`. Sem esta exceção, a metade mais importante da fila da IA
// ficaria invisível dentro da própria aba da IA. Espelha
// `Lumos::FilaDaIa::STATUS` no servidor.
const STATUS_DA_FILA_DA_IA = ['open', 'pending'];

const statusDaFilaDaIa = (statusDaConversa, statusPedido) => {
  if (statusPedido === 'all') return true;
  if (statusPedido === 'open') {
    return STATUS_DA_FILA_DA_IA.includes(statusDaConversa);
  }
  return statusDaConversa === statusPedido;
};

const getters = {
  getAllConversations: ({ allConversations, chatSortFilter: sortKey }) => {
    // Spread before sort: prevents in-place mutation of the reactive array,
    // which would cause a reactivity cascade and visual jitter on label changes.
    return [...allConversations].sort((a, b) => sortComparator(a, b, sortKey));
  },
  getFilteredConversations: (
    { allConversations, chatSortFilter, appliedFilters },
    _,
    __,
    rootGetters
  ) => {
    const currentUser = rootGetters.getCurrentUser;
    const currentUserId = rootGetters.getCurrentUser.id;
    const currentAccountId = rootGetters.getCurrentAccountId;

    const permissions = getUserPermissions(currentUser, currentAccountId);
    const userRole = getUserRole(currentUser, currentAccountId);

    return allConversations
      .filter(conversation => {
        const matchesFilterResult = matchesFilters(
          conversation,
          appliedFilters
        );
        const allowedForRole = applyRoleFilter(
          conversation,
          userRole,
          permissions,
          currentUserId
        );

        return matchesFilterResult && allowedForRole;
      })
      .sort((a, b) => sortComparator(a, b, chatSortFilter));
  },
  getSelectedChat: ({ selectedChatId, allConversations }) => {
    const selectedChat = allConversations.find(
      conversation => conversation.id === selectedChatId
    );
    return selectedChat || {};
  },
  getSelectedChatAttachments: ({ selectedChatId, attachments }) => {
    return attachments[selectedChatId] || [];
  },
  getChatListFilters: ({ conversationFilters }) => conversationFilters,
  getLastEmailInSelectedChat: (stage, _getters) => {
    const selectedChat = _getters.getSelectedChat;
    const { messages = [] } = selectedChat;
    const lastEmail = [...messages].reverse().find(message => {
      const { message_type: messageType } = message;
      if (message.private) return false;

      return [MESSAGE_TYPE.OUTGOING, MESSAGE_TYPE.INCOMING].includes(
        messageType
      );
    });

    return lastEmail;
  },
  getMineChats: (_state, _, __, rootGetters) => activeFilters => {
    const currentUserID = rootGetters.getCurrentUser?.id;

    return [..._state.allConversations]
      .filter(conversation => {
        const { assignee, assignee_type: assigneeType } = conversation.meta;
        // Achado 2026-07-29 (bug real, reportado pelo Victor: conversa da
        // Andie aparecendo em "Minhas"): User e AgentBot têm sequências de
        // id independentes — Victor (User id=1) colide com Andie (AgentBot
        // id=1). Sem checar assignee_type, qualquer conversa da Andie cujo
        // id bater com o id do usuário logado aparecia como seguido.
        const isAssignedToMe =
          assignee &&
          assignee.id === currentUserID &&
          assigneeType !== 'AgentBot';
        const shouldFilter = applyPageFilters(conversation, activeFilters);
        const isChatMine = isAssignedToMe && shouldFilter;

        return isChatMine;
      })
      .sort((a, b) => sortComparator(a, b, _state.chatSortFilter));
  },
  getAppliedConversationFiltersV2: _state => {
    // TODO: Replace existing one with V2 after migrating the filters to use camelcase
    return _state.appliedFilters.map(camelcaseKeys);
  },
  getAppliedConversationFilters: _state => {
    return _state.appliedFilters;
  },
  getAppliedConversationFiltersQuery: _state => {
    const hasAppliedFilters = _state.appliedFilters.length !== 0;
    return hasAppliedFilters ? filterQueryGenerator(_state.appliedFilters) : [];
  },
  getUnAssignedChats: _state => activeFilters => {
    return [..._state.allConversations]
      .filter(conversation => {
        const isUnAssigned = !conversation.meta.assignee;
        const shouldFilter = applyPageFilters(conversation, activeFilters);
        return isUnAssigned && shouldFilter;
      })
      .sort((a, b) => sortComparator(a, b, _state.chatSortFilter));
  },
  // advup — A FILA DA IA: conversas em que alguma das IAs tem o próximo passo.
  //
  // 🔴 ESTA FUNÇÃO FILTRAVA `meta.assignee_type === 'AgentBot'` — a mesma
  // mentira que o servidor contava, duplicada aqui no navegador (achado em
  // 18/09/2026). Era o QUARTO lugar decidindo o mesmo fato, e por isso a aba
  // continuou mostrando só o follow-up mesmo depois de o servidor passar a
  // mandar as três origens.
  //
  // Quem decide agora é o SERVIDOR, uma vez só (`Lumos::FilaDaIa`), e carimba
  // a decisão em cada conversa como `motivo_da_ia`. Aqui só se lê o carimbo.
  //
  // O `assignee_type === 'AgentBot'` fica como rede: conversa que chega por
  // evento ao vivo não passa pelo serializador da lista e ainda não traz o
  // carimbo. Sem esta segunda condição, conversa que aparecia antes sumiria —
  // e não se retira dado. Na recarga da lista, o carimbo chega e corrige.
  //
  // 🔴 21/10/2026 (Victor: "tive que dar F5"): o carimbo é da hora em que a
  // lista carregou e o evento ao vivo não o traz. Transferida para um time
  // (que dá dono) ou para uma pessoa, a conversa ficava na aba IA até o F5.
  // O servidor exige "sem dono humano" (`FilaDaIa.escopo`: assignee_id nulo);
  // o mesmo vale aqui, lido do `meta`, que o evento ao vivo atualiza.
  getBotChats: _state => activeFilters => {
    const { status, ...demaisFiltros } = activeFilters;
    const semDonoHumano = conversation =>
      !conversation.meta?.assignee ||
      conversation.meta?.assignee_type === 'AgentBot';
    return [..._state.allConversations]
      .filter(
        conversation =>
          semDonoHumano(conversation) &&
          (!!conversation.motivo_da_ia ||
            conversation.meta?.assignee_type === 'AgentBot') &&
          statusDaFilaDaIa(conversation.status, status) &&
          applyPageFilters(conversation, { ...demaisFiltros, status: 'all' })
      )
      .sort((a, b) => sortComparator(a, b, _state.chatSortFilter));
  },
  getParticipatingChats: (_state, _, __, rootGetters) => activeFilters => {
    const currentUserId = rootGetters.getCurrentUser?.id;
    const getWatchers = rootGetters['conversationWatchers/getByConversationId'];
    return _state.allConversations.filter(conversation => {
      const watchers = getWatchers(conversation.id);
      // Watchers are only loaded for the conversation open in the detail
      // panel. If loaded and current user is not in them, filter it out.
      if (watchers && !watchers.some(w => w.id === currentUserId)) {
        return false;
      }
      return applyPageFilters(conversation, activeFilters);
    });
  },
  getAllStatusChats: (_state, _, __, rootGetters) => activeFilters => {
    const currentUser = rootGetters.getCurrentUser;
    const currentUserId = rootGetters.getCurrentUser.id;
    const currentAccountId = rootGetters.getCurrentAccountId;

    const permissions = getUserPermissions(currentUser, currentAccountId);
    const userRole = getUserRole(currentUser, currentAccountId);

    return [..._state.allConversations]
      .filter(conversation => {
        const shouldFilter = applyPageFilters(conversation, activeFilters);
        const allowedForRole = applyRoleFilter(
          conversation,
          userRole,
          permissions,
          currentUserId
        );

        return shouldFilter && allowedForRole;
      })
      .sort((a, b) => sortComparator(a, b, _state.chatSortFilter));
  },
  getChatListLoadingStatus: ({ listLoadingStatus }) => listLoadingStatus,
  getChatListErrorStatus: ({ listErrorStatus }) => listErrorStatus,
  getAllMessagesLoaded(_state) {
    const [chat] = getSelectedChatConversation(_state);
    return !chat || chat.allMessagesLoaded === undefined
      ? false
      : chat.allMessagesLoaded;
  },
  getUnreadCount(_state) {
    const [chat] = getSelectedChatConversation(_state);
    if (!chat) return [];
    return chat.messages.filter(
      chatMessage =>
        chatMessage.created_at * 1000 > chat.agent_last_seen_at * 1000 &&
        chatMessage.message_type === 0 &&
        chatMessage.private !== true
    ).length;
  },
  getChatStatusFilter: ({ chatStatusFilter }) => chatStatusFilter,
  getChatSortFilter: ({ chatSortFilter }) => chatSortFilter,
  getSelectedInbox: ({ currentInbox }) => currentInbox,
  getConversationById: _state => conversationId => {
    return _state.allConversations.find(
      value => value.id === Number(conversationId)
    );
  },
  getConversationParticipants: _state => {
    return _state.conversationParticipants;
  },
  getConversationLastSeen: _state => {
    return _state.conversationLastSeen;
  },

  getContextMenuChatId: _state => {
    return _state.contextMenuChatId;
  },

  getCopilotAssistant: _state => {
    return _state.copilotAssistant;
  },
};

export default getters;
